import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getStudentAnalytics,
  getStudentAnalyticsPortal,
} from "../../api/student/studentAnalyticsApi";
import KPIGroup from "./KPIGroup";
import ChartCard from "./ChartCard";
import DonutChart from "./DonutChart";
import LineChart from "./LineChart";
import BarChart from "./BarChart";
import { useToast } from "../../context/ToastContext";
const PERIOD_OPTIONS = [
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "all", label: "All Time" },
];

const LoadingState = () => (
  <div className="flex h-40 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-6 text-center">
    <p className="text-sm text-rose-700">{message || "Failed to load analytics"}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="rounded-md bg-rose-600 px-3 py-1 text-xs text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

const StudentAnalytics = ({ isPortal = false }) => {
  const [period, setPeriod] = useState("year");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { period };
      if (classId) params.classId = classId;
      if (sectionId) params.sectionId = sectionId;

      const result = isPortal
        ? await getStudentAnalyticsPortal(params)
        : await getStudentAnalytics(params);
      setData(result);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [period, classId, sectionId, isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const classes = data?.filterOptions?.classes || [];
  const allSections = data?.filterOptions?.sections || [];

  const sections = useMemo(() => {
    if (!classId) return allSections;
    return allSections.filter(
      (s) => String(s.classId) === String(classId)
    );
  }, [allSections, classId]);

  const handleClassChange = (e) => {
    setClassId(e.target.value);
    setSectionId("");
  };

  const kpis = data?.kpis || {};
  const distributions = data?.distributions || {};
  const trends = data?.trends || {};

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-neutral-200">Student Overview</h2>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={classId}
            onChange={handleClassChange}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
          >
            <option value="">All Classes</option>
            {classes.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            className="rounded-md border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs text-slate-700 dark:text-neutral-200"
            disabled={!sections.length}
          >
            <option value="">All Sections</option>
            {sections.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
              </option>
            ))}
          </select>

          <div className="flex gap-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${ period === opt.value ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-neutral-900 text-slate-600 dark:text-neutral-300 hover:bg-slate-200" }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={fetchData} />}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KPIGroup
              title="Headcount"
              items={[
                { label: "Total", value: kpis.totalStudents },
                { label: "Active", value: kpis.activeStudents, tone: "success" },
                { label: "Enrolled", value: kpis.enrolled },
                { label: "New", value: kpis.newEnrollments, tone: "success" },
              ]}
            />
            <KPIGroup
              title="Status"
              items={[
                { label: "Left", value: kpis.left, tone: "muted" },
                { label: "Transferred", value: kpis.transferred, tone: "warning" },
                { label: "Graduated", value: kpis.graduated },
                { label: "Inactive", value: kpis.inactiveStudents, tone: "muted" },
              ]}
            />
            <KPIGroup
              title="Gender"
              items={[
                { label: "Male", value: kpis.male },
                { label: "Female", value: kpis.female },
                { label: "Other", value: kpis.other, tone: "muted" },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {!classId && (
              <ChartCard title="By Class">
                <BarChart
                  data={distributions.byClass || []}
                  layout="horizontal"
                  height={200}
                  color="#4F46E5"
                  categoryWidth={90}
                />
              </ChartCard>
            )}

            <ChartCard title={classId ? "By Section" : "Top Sections"}>
              <BarChart
                data={distributions.bySection || []}
                layout="horizontal"
                height={200}
                color="#06B6D4"
                categoryWidth={100}
              />
            </ChartCard>

            {classId && (
              <ChartCard title="Status Mix">
                <DonutChart data={distributions.byStatus || []} height={200} />
              </ChartCard>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Gender">
              <DonutChart data={distributions.byGender || []} height={180} />
            </ChartCard>
            <ChartCard title="Enrollment Trend">
              <LineChart
                data={trends.enrollmentTrend || []}
                height={180}
                dataKey="count"
                xKey="period"
              />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
};

export default StudentAnalytics;
